/**
 * Página de Logs do Sistema
 * Mostra logs de atividades para auditoria
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Search, 
  RefreshCw, 
  Filter, 
  Activity, 
  LogIn, 
  LogOut, 
  Mail, 
  MailCheck, 
  UserPlus, 
  UserMinus,
  UserCog,
  AlertCircle,
  Database,
  Camera,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api`;

type ActivityType = 
  | 'sync_data'
  | 'login'
  | 'logout'
  | 'email_generated'
  | 'email_sent'
  | 'user_created'
  | 'user_updated'
  | 'user_deleted'
  | 'snapshot_created'
  | 'config_updated'
  | 'error'
  | 'system';

interface ActivityLog {
  id: string;
  type: ActivityType;
  outcome?: 'started' | 'success' | 'failure' | 'skipped';
  action: string;
  description: string;
  user?: string;
  trigger: 'auto' | 'manual' | 'system';
  metadata?: Record<string, unknown>;
  timestamp: string;
  ip?: string;
}

interface LogsStats {
  total: number;
  last24h: number;
  last7d: number;
  errorCount: number;
  byType: Record<string, number>;
  byTrigger: Record<string, number>;
  topUsers: { user: string; count: number }[];
}

const ITEMS_PER_PAGE = 25;

// Ícones e cores por tipo de log
const LOG_CONFIG: Record<ActivityType, { icon: typeof Activity; color: string; label: string }> = {
  sync_data: { icon: Database, color: 'text-blue-500', label: 'Sincronização' },
  login: { icon: LogIn, color: 'text-green-500', label: 'Login' },
  logout: { icon: LogOut, color: 'text-gray-500', label: 'Logout' },
  email_generated: { icon: Mail, color: 'text-purple-500', label: 'Email Gerado' },
  email_sent: { icon: MailCheck, color: 'text-emerald-500', label: 'Email Enviado' },
  user_created: { icon: UserPlus, color: 'text-cyan-500', label: 'Usuário Criado' },
  user_updated: { icon: UserCog, color: 'text-amber-500', label: 'Usuário Atualizado' },
  user_deleted: { icon: UserMinus, color: 'text-red-500', label: 'Usuário Removido' },
  snapshot_created: { icon: Camera, color: 'text-indigo-500', label: 'Snapshot' },
  config_updated: { icon: Settings, color: 'text-orange-500', label: 'Configuração' },
  error: { icon: AlertCircle, color: 'text-red-600', label: 'Erro' },
  system: { icon: Activity, color: 'text-gray-400', label: 'Sistema' },
};

const TRIGGER_LABELS: Record<string, string> = {
  auto: 'Automático',
  manual: 'Manual',
  system: 'Sistema',
};

const OUTCOME_LABELS: Record<NonNullable<ActivityLog['outcome']>, string> = {
  started: 'Em andamento',
  success: 'Concluído',
  failure: 'Falhou',
  skipped: 'Ignorado',
};

function formatAuditDetails(metadata?: Record<string, unknown>): string | null {
  if (!metadata) return null;

  const details: string[] = [];
  const add = (label: string, value: unknown) => {
    if (typeof value === 'string' && value) details.push(`${label}: ${value}`);
    if (typeof value === 'number') details.push(`${label}: ${value.toLocaleString('pt-BR')}`);
  };

  add('Origem', metadata.source);
  add('Período inicial', metadata.startDate);
  add('Período final', metadata.endDate);
  add('Registros', metadata.totalRecords);
  add('Processos', metadata.processCount);
  add('Regionais', metadata.regionCount ?? metadata.regionals);
  add('E-mails enviados', metadata.emailsSent);
  add('Falhas de e-mail', metadata.emailsFailed);
  add('Motivo', metadata.error);

  if (typeof metadata.durationMs === 'number') {
    details.push(`Duração: ${(metadata.durationMs / 1000).toFixed(1)}s`);
  }
  if (typeof metadata.waitedMs === 'number') {
    details.push(`Aguardou: ${(metadata.waitedMs / 1000).toFixed(1)}s`);
  }

  const errors = metadata.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const names = errors
      .map((error) => typeof error === 'object' && error !== null && 'endpoint' in error
        ? String(error.endpoint)
        : null)
      .filter(Boolean);
    details.push(`Endpoints com erro: ${names.join(', ') || errors.length}`);
  }

  return details.length > 0 ? details.join(' · ') : null;
}

async function fetchLogs(params: {
  type?: ActivityType;
  trigger?: string;
  limit: number;
  offset: number;
}): Promise<{ data: ActivityLog[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.type) searchParams.set('type', params.type);
  if (params.trigger) searchParams.set('trigger', params.trigger);
  searchParams.set('limit', params.limit.toString());
  searchParams.set('offset', params.offset.toString());
  
  const url = `${API_BASE}/logs?${searchParams.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Erro ao buscar logs');
  const json = await res.json();
  return { data: json.result?.data || [], total: json.result?.total || 0 };
}

async function fetchStats(): Promise<LogsStats> {
  const res = await fetch(`${API_BASE}/logs/stats`);
  if (!res.ok) throw new Error('Erro ao buscar estatísticas');
  const json = await res.json();
  return json.result;
}

export function AdminLogsPage() {
  const [typeFilter, setTypeFilter] = useState<ActivityType | ''>('');
  const [triggerFilter, setTriggerFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['logs', typeFilter, triggerFilter, currentPage],
    queryFn: () => fetchLogs({
      type: typeFilter || undefined,
      trigger: triggerFilter || undefined,
      limit: ITEMS_PER_PAGE,
      offset,
    }),
    refetchInterval: 30000, // Atualiza a cada 30s
  });

  const { data: stats } = useQuery({
    queryKey: ['logs-stats'],
    queryFn: fetchStats,
    refetchInterval: 60000, // Atualiza a cada 1min
  });

  const logs = logsData?.data || [];
  const total = logsData?.total || 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleFilterChange = (type: ActivityType | '', trigger: string) => {
    setTypeFilter(type);
    setTriggerFilter(trigger);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Logs do Sistema</h2>
          <p className="text-muted-foreground">Monitore atividades e eventos do sistema</p>
        </div>
        <Button onClick={() => refetch()} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Total de Logs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-500">{stats.last24h.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Últimas 24h</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{stats.last7d.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Últimos 7 dias</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-500">{stats.errorCount.toLocaleString('pt-BR')}</div>
              <div className="text-sm text-muted-foreground">Erros</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          Filtros
        </Button>
        
        {(typeFilter || triggerFilter) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleFilterChange('', '')}
          >
            Limpar filtros
          </Button>
        )}
        
        <span className="text-sm text-muted-foreground ml-auto">
          {total.toLocaleString('pt-BR')} registros
        </span>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-4">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={typeFilter || '__all__'} onValueChange={(v) => handleFilterChange((v === '__all__' ? '' : v) as ActivityType | '', triggerFilter)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {Object.entries(LOG_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Gatilho</Label>
              <Select value={triggerFilter || '__all__'} onValueChange={(v) => handleFilterChange(typeFilter, v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="auto">Automático</SelectItem>
                  <SelectItem value="system">Sistema</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Data/Hora</TableHead>
                <TableHead className="w-[150px]">Tipo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="w-[120px]">Resultado</TableHead>
                <TableHead className="hidden md:table-cell w-[150px]">Usuário</TableHead>
                <TableHead className="w-[100px]">Gatilho</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    Nenhum log encontrado
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const config = LOG_CONFIG[log.type] || LOG_CONFIG.system;
                  const Icon = config.icon;
                  const details = formatAuditDetails(log.metadata);
                  
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className={cn('flex items-center gap-2', config.color)}>
                          <Icon className="h-4 w-4" />
                          <span className="text-sm font-medium">{config.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{log.action}</div>
                        <div className="text-xs text-muted-foreground">{log.description}</div>
                        {details && <div className="mt-1 text-xs text-muted-foreground">{details}</div>}
                      </TableCell>
                      <TableCell>
                        {log.outcome ? (
                          <Badge
                            variant={log.outcome === 'failure' ? 'destructive' : 'outline'}
                            className={cn(
                              log.outcome === 'success' && 'border-emerald-500/40 text-emerald-700',
                              log.outcome === 'started' && 'border-blue-500/40 text-blue-700',
                              log.outcome === 'skipped' && 'border-amber-500/40 text-amber-700'
                            )}
                          >
                            {OUTCOME_LABELS[log.outcome]}
                          </Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {log.user || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          log.trigger === 'manual' ? 'default' :
                          log.trigger === 'auto' ? 'secondary' : 'outline'
                        }>
                          {TRIGGER_LABELS[log.trigger] || log.trigger}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="h-8 w-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Top Users */}
      {stats && stats.topUsers.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Usuários Mais Ativos</h3>
          <div className="flex flex-wrap gap-2">
            {stats.topUsers.map((u) => (
              <div key={u.user} className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full text-sm">
                <span>{u.user}</span>
                <span className="text-muted-foreground">({u.count})</span>
              </div>
            ))}
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
